import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, TrendingUp, FileText, Trash2, Upload, CheckCircle2, X } from 'lucide-react';
import { useForecastScenarios } from '../hooks/useForecast';
import { formatFiscalYear } from '../utils/forecastEngine';
import { BudgetForecastImportModal } from '../components/BudgetForecastImportModal';
import { BudgetForecastLine } from '../utils/budgetForecastCsvParser';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function ForecastListPage() {
  const navigate = useNavigate();
  const { effectiveUserId } = useAuth();
  const { scenarios, loading, deleteScenario, loadScenarios } = useForecastScenarios();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      await deleteScenario(id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting scenario:', error);
      alert('Erreur lors de la suppression du scénario');
    }
  };

  const handleImport = async (data: BudgetForecastLine[]) => {
    if (!effectiveUserId) return;

    try {
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;

      const { data: newScenario, error: scenarioError } = await supabase
        .from('forecast_scenarios')
        .insert([{
          user_id: effectiveUserId,
          name: `Import CSV ${new Date().toLocaleDateString('fr-FR')}`,
          start_date: startDate,
          years_count: 5,
          currency: 'EUR'
        }])
        .select()
        .single();

      if (scenarioError || !newScenario) {
        console.error('Scenario error:', scenarioError);
        throw new Error('Erreur lors de la création du scénario');
      }

      const incomeLines = data.filter(line => line.type === 'Recette');
      const expenseLines = data.filter(line => line.type === 'Dépense');

      console.log(`Import: ${incomeLines.length} recettes, ${expenseLines.length} dépenses`);

      let revenueSuccess = 0;
      let revenueFailed = 0;

      for (const line of incomeLines) {
        console.log(`Importing revenue: ${line.name}`, line);
        const { data: lineItem, error: lineError } = await supabase
          .from('forecast_line_items')
          .insert([{
            scenario_id: newScenario.id,
            type: 'revenue',
            section: 'Recettes',
            label: line.name,
            rule_type: 'fixed',
            base_amount: line.year1,
            active_from_year: 1,
            active_to_year: 5
          }])
          .select()
          .single();

        if (lineError || !lineItem) {
          console.error('Error inserting revenue line:', line.name, lineError);
          revenueFailed++;
          continue;
        }

        revenueSuccess++;

        const yearValues = [line.year2, line.year3, line.year4, line.year5];
        for (let i = 0; i < yearValues.length; i++) {
          if (yearValues[i] !== line.year1) {
            const { error: overrideError } = await supabase.from('forecast_overrides').insert([{
              line_item_id: lineItem.id,
              year_index: i + 2,
              override_amount: yearValues[i]
            }]);
            if (overrideError) {
              console.error('Error inserting override:', overrideError);
            }
          }
        }
      }

      let expenseSuccess = 0;
      let expenseFailed = 0;

      for (const line of expenseLines) {
        console.log(`Importing expense: ${line.name}`, line);
        const { data: lineItem, error: lineError } = await supabase
          .from('forecast_line_items')
          .insert([{
            scenario_id: newScenario.id,
            type: 'expense',
            section: 'Dépenses',
            label: line.name,
            rule_type: 'fixed',
            base_amount: line.year1,
            active_from_year: 1,
            active_to_year: 5
          }])
          .select()
          .single();

        if (lineError || !lineItem) {
          console.error('Error inserting expense line:', line.name, lineError);
          expenseFailed++;
          continue;
        }

        expenseSuccess++;

        const yearValues = [line.year2, line.year3, line.year4, line.year5];
        for (let i = 0; i < yearValues.length; i++) {
          if (yearValues[i] !== line.year1) {
            const { error: overrideError } = await supabase.from('forecast_overrides').insert([{
              line_item_id: lineItem.id,
              year_index: i + 2,
              override_amount: yearValues[i]
            }]);
            if (overrideError) {
              console.error('Error inserting override:', overrideError);
            }
          }
        }
      }

      const summary = `Import terminé: ${revenueSuccess} recettes et ${expenseSuccess} dépenses importées`;
      console.log(summary);

      await loadScenarios();
      setImportSuccess(summary);

      setTimeout(() => setImportSuccess(null), 5000);
    } catch (error) {
      console.error('Error importing:', error);
      alert('Erreur lors de l\'import du fichier CSV');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Budget Prévisionnel 5 ans</h2>
          <p className="text-sm text-gray-600 mt-1">
            Créez et gérez vos comptes d'exploitation prévisionnels sur plusieurs années
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Upload size={20} />
            Importer CSV
          </button>
          <button
            onClick={() => navigate('/app/forecast/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Nouveau scénario
          </button>
        </div>
      </div>

      {importSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="text-green-600 flex-shrink-0" size={24} />
          <div className="flex-1">
            <p className="text-green-900 font-medium">{importSuccess}</p>
            <p className="text-sm text-green-700 mt-1">Le scénario apparaît maintenant dans la liste ci-dessous</p>
          </div>
          <button
            onClick={() => setImportSuccess(null)}
            className="text-green-600 hover:text-green-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {scenarios.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="max-w-md mx-auto">
            <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun scénario créé
            </h3>
            <p className="text-gray-600 mb-6">
              Créez votre premier budget prévisionnel pour planifier vos finances sur 5 ans
            </p>
            <button
              onClick={() => navigate('/app/forecast/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={20} />
              Créer mon premier scénario
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenarios.map(scenario => {
            const startDate = new Date(scenario.start_date);
            const firstYear = formatFiscalYear(startDate, 1);

            return (
              <div
                key={scenario.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">
                        {scenario.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={14} />
                        {firstYear}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(scenario.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Durée</span>
                      <span className="font-medium text-gray-900">
                        {scenario.years_count} ans
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Devise</span>
                      <span className="font-medium text-gray-900">{scenario.currency}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/app/forecast/${scenario.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <FileText size={16} />
                      Voir
                    </button>
                    <button
                      onClick={() => navigate(`/app/forecast/${scenario.id}/edit`)}
                      className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Éditer
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer ce scénario ? Cette action est irréversible et
              supprimera toutes les données associées (dimensions, lignes, ajustements).
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <BudgetForecastImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />
    </div>
  );
}
